import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { WorkbookResourcesTab } from '@/components/content-generator/editor/tabs/WorkbookResourcesTab';
import CreateResearchModal from '@/components/shared/CreateResearchModal';
import { useProjectNavigation } from '@/hooks/goal-planner/use-optimized-navigation';
import { ProjectOutletContext } from '@/types/project';

// Empty state imports
import { EmptyStateLayout } from '@/components/shared/empty-states/EmptyStateLayout';
import { ProjectImportResearchEmptyState } from '@/components/shared/import';
import { Microscope } from 'lucide-react';

// Import research data hook to detect empty state
import { useWorkbookResearch } from '@/hooks/content-generator/useWorkbookResearch';
import { useQuestDetection } from '@/hooks/onboarding/useQuestDetection';

/**
 * ProjectResourcesPage - Resources route for project system
 * Contains workbook resources and research functionality
 */
const ProjectResourcesPage: React.FC = () => {
  const { project, goal, agentThreadId, navigationData, registerResearchCreationHandler } = useOutletContext<ProjectOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize quest detection for this page
  useQuestDetection();
  
  // State management
  const [isCreateResearchModalOpen, setIsCreateResearchModalOpen] = useState(false);
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [isReplacementGenerating, setIsReplacementGenerating] = useState(false);
  const [autoOpenResearchId, setAutoOpenResearchId] = useState<string | null>(null);
  
  // Get research data to determine empty state
  const { 
    data: workbookResearchData = [], 
    isLoading: isResearchLoading 
  } = useWorkbookResearch(project?.id || '');
  
  // Determine if empty state should be shown
  const researchItems = workbookResearchData || [];
  
  const shouldShowCenteredLayout = !isResearchLoading && 
    researchItems.length === 0;
  
  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          Project not found.
        </p>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          This project doesn't have an associated goal. Research functionality requires a goal to be set up.
        </p>
      </div>
    );
  }
  
  const pages = navigationData?.pages || [];
  
  const handleCreateResearch = () => {
    setIsCreateResearchModalOpen(true);
  };
  
  // Check for openResearch URL parameter and auto-open research lightbox
  useEffect(() => {
    const openResearchId = searchParams.get('openResearch');
    if (openResearchId) {
      console.log('[ProjectResourcesPage] Auto-opening research lightbox from URL parameter:', openResearchId);
      
      // Store the research ID to pass to WorkbookResourcesTab
      setAutoOpenResearchId(openResearchId);
      
      // Remove the parameter from URL to clean it up
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openResearch');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Register research creation handler with the layout
  useEffect(() => {
    if (registerResearchCreationHandler && goal) {
      registerResearchCreationHandler(handleCreateResearch);
    }
    
    // Cleanup: unregister when component unmounts or goal changes
    return () => {
      if (registerResearchCreationHandler) {
        registerResearchCreationHandler(undefined);
      }
    };
  }, [registerResearchCreationHandler, goal]);
  
  const handleResearchCreated = (researchId: string) => {
    // Research will automatically appear in the research grid
    console.log('Research created:', researchId);
  };
  
  return (
    <div className="space-y-6">
      {shouldShowCenteredLayout ? (
        /* Empty State Layout - Import Research System */
        <EmptyStateLayout
          icon={<Microscope className="h-8 w-8 text-green-500 dark:text-green-400" />}
          title="Import Research"
          description="Organize your research findings in this project and share insights with your team"
          variant="green"
        >
          <ProjectImportResearchEmptyState 
            projectId={project.id}
            onImportComplete={() => {
              // The page will automatically refresh when items are imported
              console.log('Research imported successfully');
            }}
          />
        </EmptyStateLayout>
      ) : (
        /* Normal layout when research exists */
        <div className="space-y-6">
          <WorkbookResourcesTab 
            pageId={project.id}
            onCreateResearch={handleCreateResearch}
            autoOpenResearchId={autoOpenResearchId}
            onAutoOpenComplete={() => setAutoOpenResearchId(null)}
          />

        </div>
      )}
      
      {/* Create Research Modal */}
      <CreateResearchModal
        isOpen={isCreateResearchModalOpen}
        onClose={() => setIsCreateResearchModalOpen(false)}
        goalId={goal.id}
        goalTitle={goal.title}
        goalDescription={goal.description}
        onResearchCreated={handleResearchCreated}
      />
    </div>
  );
};

export default ProjectResourcesPage; 